// src/auth/useAccessFeatures.ts
import { useGetIdentity } from 'react-admin';
import { ROLES, getEffectiveTabs, type UserRole } from './roles';
export { canUseFeature } from './canUseFeature';

export interface AccessFeatures {
    role: UserRole | undefined;
    features: string[];
    /** No identity result yet: first mount, retrying, or offline-paused. */
    isPending: boolean;
}

// Single source of the role -> effective-features computation shared by the
// route guard, the default landing route and the header tabs. Keeping it in
// one place stops the three from drifting on a security-relevant decision.
export function useAccessFeatures(): AccessFeatures {
    const { identity, isPending } = useGetIdentity();

    // isPending is the flag that means "no result yet". isLoading is
    // (isPending && isFetching), so it is false while a query is retrying or
    // has errored — states where identity is still undefined and treating the
    // user as feature-less would wrongly deny them.
    if (isPending || !identity) {
        return { role: undefined, features: [], isPending: Boolean(isPending) };
    }

    // Membership test rather than a truthiness check on ROLE_DEFAULT_TABS[role],
    // which would also accept inherited Object.prototype keys.
    const rawRole = identity.role as string | undefined;
    const role = (ROLES as readonly string[]).includes(rawRole ?? '')
        ? (rawRole as UserRole)
        : undefined;
    const allowedFeatures: string[] = Array.isArray(identity.allowed_features)
        ? identity.allowed_features
        : [];

    return {
        role,
        features: role ? getEffectiveTabs(role, allowedFeatures) : [],
        isPending: false,
    };
}
